import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Home as HomeIcon,  Briefcase, Share2, Sun, Moon, Mail, Github, Linkedin, Phone } from 'lucide-react';
import Home from './pages/Home';
import CV from './pages/CV';

const Portfolio = () => {
  const [theme, setTheme] = useState('light');
  const [showSocial, setShowSocial] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const saved = localStorage.getItem('theme') || 'light';
    setTheme(saved);
  }, []);

  useEffect(() => {
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(theme === 'light' ? 'dark' : 'light');

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation before scrolling
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const bgColor = theme === 'light' ? 'bg-white' : 'bg-[#0a0a0a]';
  const textColor = theme === 'light' ? 'text-gray-900' : 'text-gray-100';
  const textSecondary = theme === 'light' ? 'text-gray-600' : 'text-gray-400';
  const borderColor = theme === 'light' ? 'border-gray-200' : 'border-gray-800';
  const hoverBg = theme === 'light' ? 'hover:bg-gray-50' : 'hover:bg-[#1a1a1a]';

  const projects = [
    {
      title: 'SignBonga',
      description: 'E-learning platform for sign language education targeting marginalized communities. Won Microsoft ADC/ALX Africa/UON Hackathon.',
      tech: ['Django', 'React', 'PostgreSQL'],
      link: 'https://signbonga.vercel.app/',
      github: null
    },
    {
      title: 'M-Pesa Analyzer Pro',
      description: 'Streamlit application for analyzing M-Pesa statements with AI-powered financial insights using Google Gemini.',
      tech: ['Python', 'Streamlit', 'Gemini AI'],
      link: null,
      github: 'https://github.com/ru0ya/mpesaView'
    },
    {
      title: 'Chess Assist',
      description: 'Intelligent chess coaching system powered by multi-agent AI architecture and Stockfish 17 engine.',
      tech: ['TypeScript', 'Mastra', 'Gemini AI'],
      link: null,
      github: 'https://github.com/ru0ya/chessAssist'
    },
    {
      title: 'Country Currency API',
      description: 'Go REST API that fetches country metadata and exchange rates with filtering and sorting capabilities.',
      tech: ['Go', 'Gin', 'MySQL'],
      link: null,
      github: 'https://github.com/ru0ya/countryCurrency'
    },
    {
      title: 'SeekMa3',
      description: 'Geospatial application helping Nairobi commuters find optimal public transport routes.',
      tech: ['Django', 'React', 'PostGIS'],
      link: null,
      github: 'https://github.com/ru0ya/SeekMa3'
    },
	{
		title: 'Django Recall Server',
		description: 'An application that democratizes access and contribution to constitutional matters while encouraging transparency and integrity',
		tech: ['DjangoRest Framework', 'Docker', 'PostgreSql'],
		link: null,
		github: 'https://github.com/Friendsofthepeople/django-recall-server'
	},
	{
		title: 'Nairobi Network Interventions',
		description: 'Simulation-based ranking of road network interventions for Nairobi, Kenya. Uses UXsim mesoscopic traffic simulation and greedy search to identify which planned or proposed interventions — missing links, BRT corridors, highway upgrades — produce the greatest reduction in peak-hour travel time.',
		tech: ['Python', 'UXsim', 'OSMnx', 'Pandas', 'Greedy Search'],
		link: null,
		github: 'https://github.com/ru0ya/UXsim-Nairobi'
	},
  ];

  const experiences = [
    {
      role: 'Backend Engineer',
      company: 'DigitalQatalyst',
      period: 'Feb 2025 - March 2026',
      description: 'Architecting scalable e-commerce infrastructure with Vendure, building Azure integrations with D365 CRM, and developing GraphQL APIs enabling $2M+ monthly B2B transactions with 99.9% uptime.'
    },
    {
      role: 'Backend Software Engineer',
      company: 'HNG',
      period: '2023',
      description: 'Engineered Flask RESTful APIs with JWT authentication serving 1,000+ daily users. Built Django backend with Celery task queues, achieving 85%+ test coverage and <150ms API response times.'
    },
    {
      role: 'Data Engineering Intern',
      company: 'Ramana AI',
      period: '2023',
      description: 'Built PySpark pipelines processing 10M+ daily transactions, architected Kafka streaming handling 50,000+ events/second, and developed fraud detection algorithms reducing losses across annual volume.'
    }
  ];

  return (
    <div className={`min-h-screen ${bgColor} ${textColor} transition-colors duration-300`}>
      <Routes>
        <Route path="/" element={<Home projects={projects} experiences={experiences} />} />
        <Route path="/cv" element={<CV />} />
      </Routes>

      {/* Bottom Navigation Bar */}
      <nav className={`fixed bottom-6 left-1/2 transform -translate-x-1/2 ${bgColor} border ${borderColor} rounded-full shadow-lg px-6 py-3`}>
        <div className="flex items-center gap-4">
          <button
            onClick={() => scrollToSection('home')}
            className={`p-2 rounded-full hover:scale-110 transition-all duration-200 ${location.pathname === '/' ? textColor : textSecondary}`}
            aria-label="Home"
          >
            <HomeIcon className="w-4 h-4" />
          </button>
			{/*<button
            onClick={() => navigate('/cv')}
            className={`p-2 rounded-full hover:scale-110 transition-all duration-200 ${location.pathname === '/cv' ? textColor : textSecondary}`}
            aria-label="CV"
          >
            <FileText className="w-4 h-4" />
          </button>*/}
          <button
            onClick={() => scrollToSection('projects')}
            className={`p-2 rounded-full hover:scale-110 transition-all duration-200 ${location.pathname === '/' ? textSecondary : textSecondary} hover:${textColor}`}
            aria-label="Projects"
          >
            <Briefcase className="w-4 h-4" />
          </button>
          <div className={`w-px h-6 ${borderColor}`}></div>
          <button
            onClick={() => setShowSocial(!showSocial)}
            className={`p-2 rounded-full hover:scale-110 transition-all duration-200 ${textSecondary} hover:${textColor}`}
            aria-label="Social"
          >
            <Share2 className="w-4 h-4" />
          </button>
          <button
            onClick={toggleTheme}
            className={`p-2 rounded-full hover:scale-110 transition-all duration-200 ${textSecondary} hover:${textColor}`}
            aria-label="Toggle theme"
          >
            {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
          </button>
        </div>
      </nav>

      {/* Social Popup */}
      {showSocial && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowSocial(false)}>
          <div className={`${bgColor} p-8 rounded-2xl max-w-sm w-full mx-4`} onClick={(e) => e.stopPropagation()}>
            <h3 className="text-2xl font-bold mb-6">Connect</h3>
            <div className="space-y-3">
              <a
                href="mailto:mwangiruoya@gmail.com"
                className={`flex items-center gap-4 p-3 rounded-lg ${hoverBg} transition-colors`}
              >
                <Mail className="w-5 h-5" />
                <span>Email</span>
              </a>
              <a
                href="https://github.com/ru0ya"
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-4 p-3 rounded-lg ${hoverBg} transition-colors`}
              >
                <Github className="w-5 h-5" />
                <span>GitHub</span>
              </a>
              <a
                href="https://www.linkedin.com/in/victor-mwangi-ruoya/"
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-4 p-3 rounded-lg ${hoverBg} transition-colors`}
              >
                <Linkedin className="w-5 h-5" />
                <span>LinkedIn</span>
              </a>
              <a
                href="tel:+254715067724"
                className={`flex items-center gap-4 p-3 rounded-lg ${hoverBg} transition-colors`}
              >
                <Phone className="w-5 h-5" />
                <span>+254 715 067 724</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Portfolio;
